<template>
  <b-row class="AuditLog mb-5">
    <b-col cols="12" lg="10" offset-lg="1">
      <Notification v-bind.sync="notification" />

      <b-card>
      <b-card-title title-tag="h2" class="mb-5 text-center">
        AUDIT LOG
      </b-card-title>

      <Datatable
        class="mt-4"
        :resources="logsList"
        :options="datatableOptions"
        @update:perPage="updatePerPage"
        @updateSort="updateSort"
        @updatePage="updatePage"
      >
        <!-- IN LINE WITH PER PAGE INPUT -->
        <template v-slot:beside-per-page>
          <b-button
            squared
            variant="outline-primary"
            size="sm"
            class="float-sm-right"
            :disabled="isLoadingRequest"
            @click="fetchData"
          >
            <v-icon name="sync" />
            <span>Refresh Audit Log Data</span>
          </b-button>
        </template>

        <!-- HEAD -->
        <template v-slot:thead-top>
          <tr>
            <th>
              <b-button
                title="Search"
                squared
                block
                size="sm"
                variant="outline-primary"
                :disabled="isLoadingRequest"
                @click="fetchData"
              >
                <v-icon name="search" />
              </b-button>
            </th>
            <th>
              <b-form-input
                size="sm"
                class="border-dark rounded-0"
                placeholder="Search Action"
                title="Search Action"
                v-model="search.action"
                :disabled="isLoadingRequest"
                @keyup="enterSearch"
              />
            </th>
            <th>
              <b-form-input
                size="sm"
                class="border-dark rounded-0"
                placeholder="Search Username"
                title="Search Username"
                v-model="search.username"
                :disabled="isLoadingRequest"
                @keyup="enterSearch"
              />
            </th>
            <th>
              <b-form-input
                size="sm"
                class="border-dark rounded-0"
                placeholder="Search User Group"
                title="Search User Group"
                v-model="search.usergroup"
                :disabled="isLoadingRequest"
                @keyup="enterSearch"
              />
            </th>
            <th>
              <b-input-group title="Search Log Date">
                <flat-pickr
                  ref="logDatePicker"
                  id="log_date"
                  v-model="search.logDate"
                  class="border-dark rounded-0 form-control-sm"
                  placeholder="Search Log Date"
                  :disabled="isLoadingRequest"
                  :config="{
                    dateFormat: 'Y-m-d',
                    altFormat: 'F j Y, l',
                    altInput: true,
                    allowInput: true,
                  }"
                />
                <b-input-group-append title="Clear Log Date">
                  <b-button
                    squared
                    size="sm"
                    variant="outline-danger"
                    :disabled="isLoadingRequest"
                    @click="clearLogDatePicker"
                  >
                    <v-icon name="calendar-times" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </th>
          </tr>
        </template>

        <!-- BODY -->
        <template v-slot:cell(num)="data">
          {{ $get(data, 'item.id', 0) }}
        </template>
      </Datatable>
    </b-card>
    </b-col>
  </b-row>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Datatable from '@/components/Datatable';
import Notification from '@/components/Notification';
import ResourceService from '@/services/ResourceService';

const resource = new ResourceService();

export default {
  name: 'AuditLog',
  components: {
    Datatable,
    Notification,
  },
  mounted() {
    this.fetchData();
  },
  data() {
    return {
      perPage: 10,
      page: 1,
      total: 0,
      sortBy: 'logDate',
      sortDesc: true,
      logsList: [],
      search: {
        action: '',
        username: '',
        usergroup: '',
        logDate: '',
      },
    };
  },
  computed: {
    datatableOptions() {
      return {
        dtControl: true,
        small: true,
        pagination: true,
        fields: this.tableFields || [],
        loading: this.isLoadingRequest,
        trClass: this.rowClass,
        page: this.page,
        perPage: this.perPage,
        perPageDisabled: this.isLoadingRequest,
        total: this.total,
      };
    },
    tableFields() {
      return [
        {
          key: 'num',
          label: '#',
          class: 'text-center',
        },
        {
          key: 'action',
          label: 'Action',
        },
        {
          key: 'username',
          label: 'Username',
        },
        {
          key: 'userGroup',
          label: 'Usergroup',
        },
        {
          key: 'logDate',
          label: 'Log Date',
          sortable: true,
          formatter: val => this.$fd(val, 'MMM DD YYYY - h:mm:ss A'),
          class: 'text-center',
        },
        // {
        //   key: 'actions',
        //   label: 'Actions',
        //   sortable: false,
        //   class: 'text-center',
        // },
      ];
    },
    searchFieldOptions() {
      return {
        tagClass: 'row',
        labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-2',
        inputClass: 'form-control form-control-sm no-outline border-dark border px-2 rounded-0',
        type: 'text',
      };
    },
    hasSearchInput() {
      const searchKeys = Object.keys(this.search);
      return searchKeys.some(k => this.search[k]);
    },
    ...mapState(['notification']),
    ...mapGetters(['isLoadingRequest']),
  },
  methods: {
    enterSearch(e) {
      if (e.keyCode === 13) {
        this.fetchData();
      }
    },
    clearLogDatePicker() {
      // eslint-disable-next-line no-underscore-dangle
      const dp = document.querySelector('#log_date')._flatpickr;
      dp.setDate(null);
      this.search.logDate = '';
    },
    updateSort(payload) {
      this.sortBy = this.$get(payload, 'sortBy', null);
      this.sortDesc = this.$get(payload, 'sortDesc', false);
      this.fetchData();
    },
    updatePerPage(val) {
      this.perPage = val;
      this.page = 1;
      this.fetchData();
    },
    updatePage(val) {
      this.page = val;
      this.fetchData();
    },
    /**
     * Fetch audit logs
     */
    fetchData() {
      // build query params
      const params = {
        page: this.page,
        perPage: this.perPage,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        filter: this.search,
      };

      this.resetNotification();
      this.startLoadingRequest();
      // fetch all audit logs
      resource.get('/system/audit-logs', params)
        .then(({
          success,
          data,
          message,
          total,
        }) => {
          if (!success) {
            return this.setNotification({
              show: true,
              dismissible: true,
              variant: 'danger',
              message: message || 'An error occurred',
              icon: { name: 'exclamation-triangle' },
            });
          }
          this.logsList = data;
          this.total = total;

          return success;
        })
        .catch(() => {
          this.setNotification({
            show: true,
            dismissible: true,
            variant: 'danger',
            message: 'An error occurred',
            icon: { name: 'exclamation-triangle' },
          });
        })
        .then(() => this.stopLoadingRequest());
    },
    ...mapActions([
      'resetNotification',
      'setNotification',
      'startLoadingRequest',
      'stopLoadingRequest',
    ]),
  },
};
</script>
